import { useMutation, useQuery } from '@tanstack/react-query';
import { KIND as ButtonKind } from 'baseui/button';
import {
  Modal,
  ModalBody,
  ModalButton,
  ModalFooter,
  ModalHeader,
} from 'baseui/modal';
import { Select } from 'baseui/select';
import { Banner, KIND as BannerKind } from 'components/Banner';
import { FormController } from 'components/FormController';
import Text from 'components/Text';
import Config from 'config/Config';
import { format } from 'date-fns';
import Helper from 'helper/Helper';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { deserializeObservationDefect } from 'services/api/inspection/deserializer';
import { ObservationDefect } from 'services/api/inspection/types';
import {
  fetcher,
  fetcherBlob,
  FetcherMutatergs,
  FetcherQueryArgs,
} from 'services/fetcher';
import { useStitches } from 'theme';
import { getErrorMessage } from 'utils/error';
import { useQuerystring } from 'utils/hooks';

const isStructuralDefect = (defect: ObservationDefect) =>
  defect?.type === 'STRUCTURAL';

type FormFields = {
  order: 'asc' | 'desc';
  sortCriteria: string;
};

type GeneratedReport = {
  date: Date;
  id: string;
  link: string;
};

type DownloadReportArgs = FetcherMutatergs<GeneratedReport>;
const downloadReport = async ({ payload }: DownloadReportArgs) => {
  const blob = await fetcherBlob(payload.link);
  let a = document.createElement('a');
  a.href = window.URL.createObjectURL(blob);
  a.download = `${format(payload.date, 'MMMM-dd-yyyy')}-${
    payload.id
  }-Inspection Report.docx`;
  a.click();
};

type GenertateInspectionReportPayload = FetcherMutatergs<{
  uuid: string;
  structuralDefects: Record<string, number>;
  maintainanceDefects: Record<string, number>;
}>;
const generateReport = ({ payload }: GenertateInspectionReportPayload) => {
  return fetcher(Helper.parse(Config.INSPECTION_REPORT_API, payload.uuid), {
    method: 'post',
    body: JSON.stringify({
      defectsOrder: payload.structuralDefects,
      maintenancesOrder: payload.maintainanceDefects,
    }),
  }).then((r) => {
    const report = {
      date: new Date(r.date),
      id: r.id,
      link: r.link,
    } as GeneratedReport;
    return downloadReport({ payload: report });
  });
};

const useGenerateInspectionReport = () => useMutation({
  mutationFn: generateReport
});
const useDownloadReport = () => useMutation({
  mutationFn: downloadReport
});

export default function InspectionReport() {
  const { css } = useStitches();
  const navigate = useNavigate();
  const searchParams = useQuerystring();
  const hasMatch = searchParams.get('modal') === 'generate';
  const uuid = searchParams.get('inspectionId');

  const generateInspectionReport = useGenerateInspectionReport();
  const {
    control,
    handleSubmit,
    watch,
    reset: resetForm,
  } = useForm<FormFields>({
    defaultValues: {
      order: 'asc',
    },
  });
  const [sortOrder, sortCriteria] = watch(['order', 'sortCriteria']);

  const [isOpen, setIsOpen] = useState(false);

  const defectsQuery = useInspectionDefects(
    isOpen && uuid
      ? { params: { id: uuid, sortCriteria: sortCriteria ?? '', order: sortOrder } }
      : undefined,
  );

  const handleClose = () => {
    setIsOpen(false);
    generateInspectionReport.reset();
    resetForm();
    setTimeout(() => {
      searchParams.delete('modal');
      searchParams.delete('inspectionId');
      navigate(`?${searchParams.toString()}`, { replace: true });
    }, 100);
  };

  const onSubmit = handleSubmit(() => {
    if (uuid && defectsQuery.data) {
      const defects = defectsQuery.data;
      generateInspectionReport.mutate({
        payload: {
          uuid,
          structuralDefects: defects
            .filter((defect) => isStructuralDefect(defect))
            .reduce(
              (all, defect, index) => ({ ...all, [defect.uuid]: index }),
              {} as Record<string, number>,
            ),
          maintainanceDefects: defects
            .filter((defect) => !isStructuralDefect(defect))
            .reduce(
              (all, defect, index) => ({ ...all, [defect.uuid]: index }),
              {} as Record<string, number>,
            ),
        },
      });
    }
  });

  // TODO: Enable rule of hooks in eslint
  // FIXME: This is code block repeats
  useEffect(() => {
    if (hasMatch) {
      setIsOpen(true);
      return;
    }
    setIsOpen(false);
    // Only strip the param when present, so the navigation below cannot
    // re-trigger this effect in a loop once it lands on the cleaned URL.
    if (searchParams.has('modal')) {
      const timeoutId = setTimeout(() => {
        searchParams.delete('modal');
        navigate(`?${searchParams.toString()}`, { replace: true });
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [hasMatch, searchParams, navigate]);

  return (
    <Modal
      onClose={() => handleClose()}
      overrides={{
        Dialog: {
          style: {
            width: '30vw',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
      isOpen={isOpen}
    >
      <ModalHeader>
        <Text variant="HeadingSmall">Generate Report</Text>
        <Text variant="ParagraphMedium">Configure report generation order</Text>
      </ModalHeader>
      <ModalBody className={css({ layout: 'stack' })}>
        <Banner
          show={generateInspectionReport.isError}
          kind={BannerKind.negative}
          title="Error"
        >
          {generateInspectionReport.error
            ? getErrorMessage(generateInspectionReport.error)
            : 'Failed to generate report. Try again.'}
        </Banner>
        <form
          id="inspection-generate-report"
          onSubmit={onSubmit}
          className={css({ layout: 'stack' })}
        >
          <div className={css({ layout: 'row', spaceX: '$scale600' })}>
            <FormController
              control={control}
              name="sortCriteria"
              label="Sort By"
              defaultValue=""
            >
              {({ field }) => (
                <Select
                  value={field.value ? [{ id: field.value }] : undefined}
                  placeholder="Select sort type"
                  options={[
                    { label: 'Stationing', id: 'STATIONING' },
                    { label: 'Location ID', id: 'LOCATION' },
                    { label: 'Inspected Date', id: 'INSPECTED_DATE' },
                    {
                      label: 'Component & Sub-Component',
                      id: 'COMPONENT_SUBCOMPONENT',
                    },
                  ]}
                  onChange={({ option }) => field.onChange(option?.id ?? '')}
                />
              )}
            </FormController>
            <FormController
              control={control}
              name="order"
              label="Order"
              defaultValue="asc"
            >
              {({ field }) => (
                <Select
                  value={[{ id: field.value }]}
                  onChange={({ option }) => field.onChange(option?.id ?? 'asc')}
                  options={[
                    { label: 'Ascending', id: 'asc' },
                    { label: 'Descending', id: 'desc' },
                  ]}
                  clearable={false}
                  creatable={false}
                />
              )}
            </FormController>
          </div>
        </form>
      </ModalBody>
      <ModalFooter className={css({ flexShrink: 0 })}>
        <ModalButton kind={ButtonKind.tertiary} onClick={handleClose}>
          Cancel
        </ModalButton>
        <ModalButton
          form="inspection-generate-report"
          isLoading={generateInspectionReport.isPending || defectsQuery.isLoading}
          disabled={defectsQuery.isError}
        >
          Generate Report
        </ModalButton>
      </ModalFooter>
    </Modal>
  );
}

type InspectionDefectsParams = FetcherQueryArgs<{
  id: string;
  sortCriteria: string;
  order: 'asc' | 'desc';
}>;

const getInspectionDefects = (args: InspectionDefectsParams) => {
  const { id, sortCriteria, order } = args.params;

  const searchParams = new URLSearchParams(
    Object.entries({
      filter: sortCriteria ?? '',
      descending: order === 'desc' ? 'true' : 'false',
    })
      .filter(([key, value]) => Boolean(value))
      .reduce(
        (all, [key, value]) => ({ ...all, [key]: value }),
        {} as Record<string, string>,
      ),
  );
  return fetcher(
    `api/v2/inspection/${id}/defects?${searchParams.toString()}`,
  ).then((r: any[]) => r.map(deserializeObservationDefect));
};

const useInspectionDefects = (args?: InspectionDefectsParams) => {
  const params = args?.params;
  return useQuery({
    queryKey: [
      'inspection',
      params?.id ?? '',
      'defects',
      [params?.order, params?.sortCriteria],
    ],

    queryFn: () =>
      getInspectionDefects({
        params: {
          id: params?.id ?? '',
          sortCriteria: params?.sortCriteria ?? '',
          order: params?.order ?? 'asc',
        },
      }),

    enabled: !!args?.params.id,
    gcTime: 0
  });
};
